/**
 * @module features/upload/file-categories
 * @description Manages file category fetching and dropdown population for upload forms
 */

import { logger } from '../../utils/logger.js';
import { get } from '../../core/api/api-client.js';
import { ENTITY_TYPES } from '../entity/entity-types.js';
import { getCurrentEntityType } from './upload-manager.js';
import { setUploadConstraints } from './upload-constraints.js';

// Module state
let fileCategories = [];

/**
 * Fetch file categories from the API
 * @param {Object} _user - Firebase auth user object
 * @returns {Promise<Array>} Array of file category objects
 */
export async function fetchFileCategories(_user) {
    try {
        const response = await get('/api/file-categories');

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            throw new Error(`Failed to fetch categories: ${response.status} - ${errorText}`);
        }

        const data = await response.json();

        // Handle both possible response formats:
        // Format 1: { categories: [...], uploadConstraints: {...} }
        // Format 2: [...]
        // Older API builds return the bare array and report no constraints.
        if (Array.isArray(data)) {
            fileCategories = data;
            setUploadConstraints(null);
        } else if (data.categories && Array.isArray(data.categories)) {
            fileCategories = data.categories;
            setUploadConstraints(data.uploadConstraints);
        } else {
            fileCategories = [];
            setUploadConstraints(null);
        }

        // Additional validation
        if (!Array.isArray(fileCategories)) {
            fileCategories = [];
        }

        // After loading categories, refresh existing dropdowns
        refreshAllCategoryDropdowns();

        return fileCategories;
    } catch (error) {
        logger.error('Error fetching file categories:', error);
        throw error;
    }
}

/**
 * Refresh all existing category dropdowns with the loaded categories
 */
export function refreshAllCategoryDropdowns() {
    const categorySelects = document.querySelectorAll('.upload-file-category select');

    categorySelects.forEach((select) => {
        // Remove all options except the first one (placeholder)
        while (select.options.length > 1) {
            select.removeChild(select.lastElementChild);
        }

        // Add category options
        fileCategories.forEach(cat => {
            const option = document.createElement('option');
            option.value = cat.id;
            option.textContent = cat.name;
            select.appendChild(option);
        });
    });
}

/**
 * Get the currently loaded file categories
 * @returns {Array} Array of file category objects
 */
export function getFileCategories() {
    return fileCategories;
}

/**
 * Check if categories have been loaded
 * @returns {boolean} True if categories are loaded
 */
export function hasCategoriesLoaded() {
    return fileCategories.length > 0;
}

/**
 * Populate a single category select dropdown
 * @param {HTMLSelectElement} selectElement - The select element to populate
 * @param {string} selectedCategoryId - The category ID to pre-select
 */
export function populateCategorySelect(selectElement, selectedCategoryId = '') {
    // Clear existing options except placeholder
    while (selectElement.options.length > 1) {
        selectElement.removeChild(selectElement.lastElementChild);
    }

    // Add category options
    fileCategories.forEach((cat) => {
        const option = document.createElement('option');
        option.value = cat.id;
        option.textContent = cat.name;
        if (cat.id === selectedCategoryId) {
            option.selected = true;
        }
        selectElement.appendChild(option);
    });
}

/**
 * Get default category ID (first category or entity-specific default)
 * @returns {string} Default category ID or empty string
 */
export function getDefaultCategoryId() {
    // Check if current entity type has a default category configured
    const entityType = getCurrentEntityType();
    const entityConfig = entityType ? ENTITY_TYPES[entityType] : null;

    logger.debug('getDefaultCategoryId called', {
        entityType,
        defaultCategory: entityConfig?.defaultCategory,
        categoriesLoaded: fileCategories.length
    });

    if (entityConfig?.defaultCategory) {
        // Normalize names: remove hyphens/spaces and lowercase for flexible matching
        // This allows "dc-photos" to match "DC PHOTO", "dc photo", "DC-PHOTO", etc.
        const normalizeName = (name) => name.toLowerCase().replace(/[-\s]/g, '');
        const normalizedDefault = normalizeName(entityConfig.defaultCategory);

        // Find category by normalized name matching
        const defaultCategory = fileCategories.find(
            cat => cat.name && normalizeName(cat.name) === normalizedDefault
        );

        if (defaultCategory) {
            logger.debug('Using entity-specific default category', {
                entityType,
                categoryName: defaultCategory.name,
                categoryId: defaultCategory.id,
                configValue: entityConfig.defaultCategory
            });
            return defaultCategory.id;
        } else {
            logger.warn('Default category not found in available categories', {
                entityType,
                searchingFor: entityConfig.defaultCategory,
                normalizedSearch: normalizedDefault,
                availableCategories: fileCategories.map(c => ({ name: c.name, normalized: normalizeName(c.name) }))
            });
        }
    }

    // Fall back to first category
    return fileCategories[0]?.id || '';
}
